import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HeaderContainer = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: ${props => props.scrolled ? props.theme.colors.white : 'transparent'};
  box-shadow: ${props => props.scrolled ? props.theme.shadows.small : 'none'};
  transition: all ${props => props.theme.transitions.medium};
  padding: ${props => props.scrolled ? '0.75rem 0' : '1.5rem 0'};
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    padding: 0 ${props => props.theme.spacing.sm};
  }
`;

const Logo = styled(Link)`
  font-family: ${props => props.theme.fonts.secondary};
  font-size: ${props => props.theme.fontSizes.xlarge};
  font-weight: 700;
  text-decoration: none;
  transition: color ${props => props.theme.transitions.short};
  display: flex;
  flex-wrap: nowrap;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.large};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.medium};
  }
  
  .ai-part {
    color: ${props => props.theme.colors.accent};
    transition: color ${props => props.theme.transitions.short};
  }
  
  .automation-part {
    color: ${props => props.scrolled ? props.theme.colors.darkGray : props.theme.colors.white};
    transition: color ${props => props.theme.transitions.short};
  }
  
  &:hover {
    .ai-part {
      color: ${props => props.scrolled ? props.theme.colors.darkGray : props.theme.colors.white};
    }
    
    .automation-part {
      color: ${props => props.theme.colors.accent};
    }
  }
`;

const NavItems = styled.nav`
  display: flex;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: ${props => props.mobileMenuOpen ? 'flex' : 'none'};
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${props => props.theme.colors.darkGray};
    z-index: 90;
  }
`;

const NavItem = styled(motion.div)`
  margin: 0 ${props => props.theme.spacing.md};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin: ${props => props.theme.spacing.lg} 0;
  }
`;

const NavLink = styled.a`
  font-weight: 500;
  color: ${props => props.scrolled && !props.mobileMenuOpen ? props.theme.colors.darkGray : props.theme.colors.white};
  text-decoration: none;
  position: relative;
  cursor: pointer;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: ${props => props.active ? '100%' : '0'};
    height: 2px;
    background-color: ${props => props.theme.colors.accent};
    transition: width ${props => props.theme.transitions.short};
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 100;
  padding: ${props => props.theme.spacing.xs};
  margin-left: ${props => props.theme.spacing.xs};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: block;
  }
`;

const MenuIcon = styled.div`
  width: 24px;
  height: 2px;
  background-color: ${props => props.mobileMenuOpen ? 'transparent' : (props.scrolled ? props.theme.colors.darkGray : props.theme.colors.white)};
  position: relative;
  transition: all ${props => props.theme.transitions.short};
  
  &::before, &::after {
    content: '';
    width: 24px;
    height: 2px;
    background-color: ${props => props.scrolled ? props.theme.colors.darkGray : props.theme.colors.white};
    position: absolute;
    transition: all ${props => props.theme.transitions.short};
  }
  
  &::before {
    top: ${props => props.mobileMenuOpen ? '0' : '-8px'};
    transform: ${props => props.mobileMenuOpen ? 'rotate(45deg)' : 'none'};
  }
  
  &::after {
    bottom: ${props => props.mobileMenuOpen ? '0' : '-8px'};
    transform: ${props => props.mobileMenuOpen ? 'rotate(-45deg)' : 'none'};
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownContent = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: absolute;
  top: 100%;
  right: 0;
  background-color: ${props => props.theme.colors.white};
  min-width: 200px;
  box-shadow: ${props => props.theme.shadows.medium};
  border-radius: 4px;
  padding: 0.5rem 0;
  z-index: 1000;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    position: static;
    box-shadow: none;
    background-color: transparent;
  }
`;

const DropdownLink = styled.a`
  display: block;
  padding: 0.5rem 1rem;
  color: ${props => props.theme.colors.darkGray};
  text-decoration: none;
  transition: background-color ${props => props.theme.transitions.short};
  
  &:hover {
    background-color: ${props => props.theme.colors.lightGray};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    color: ${props => props.theme.colors.white};
    text-align: center;
    
    &:hover {
      background-color: ${props => props.theme.colors.darkGray};
    }
  }
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
      
      // Only update active section on home page
      if (location.pathname === '/') {
        const sections = ['hero', 'projects', 'about', 'contact'];
        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 100 && rect.bottom >= 100) {
              setActiveSection(section);
              break;
            }
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
  
  useEffect(() => {
    // Close mobile menu when route changes
    setMobileMenuOpen(false);
  }, [location]);
  
  const handleNavigation = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          window.scrollTo({
            top: element.offsetTop - 80,
            behavior: 'smooth'
          });
          setActiveSection(sectionId);
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        window.scrollTo({
          top: element.offsetTop - 80,
          behavior: 'smooth'
        });
        setActiveSection(sectionId);
      }
    }
    setMobileMenuOpen(false);
  };
  
  return (
    <HeaderContainer
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      scrolled={scrolled}
    >
      <HeaderContent>
        <Logo to="/" scrolled={scrolled}>
          <span className="ai-part">AI</span>
          <span className="automation-part">AUTOMATION</span>
        </Logo>
        
        <MobileMenuToggle onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <MenuIcon mobileMenuOpen={mobileMenuOpen} scrolled={scrolled} />
        </MobileMenuToggle>
        
        <NavItems mobileMenuOpen={mobileMenuOpen}>
          <NavItem>
            <NavLink 
              onClick={() => handleNavigation('hero')}
              active={activeSection === 'hero' && location.pathname === '/'} 
              scrolled={scrolled}
              mobileMenuOpen={mobileMenuOpen}
            >
              Home
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink 
              onClick={() => handleNavigation('about')}
              active={activeSection === 'about' && location.pathname === '/'} 
              scrolled={scrolled}
              mobileMenuOpen={mobileMenuOpen}
            >
              About
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink 
              onClick={() => handleNavigation('projects')}
              active={activeSection === 'projects' && location.pathname === '/'} 
              scrolled={scrolled}
              mobileMenuOpen={mobileMenuOpen}
            >
              Projects
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink 
              onClick={() => handleNavigation('contact')}
              active={activeSection === 'contact' && location.pathname === '/'} 
              scrolled={scrolled}
              mobileMenuOpen={mobileMenuOpen}
            >
              Contact
            </NavLink>
          </NavItem>
          <NavItem>
            <DropdownContainer 
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <NavLink 
                onClick={() => setDropdownOpen(!dropdownOpen)}
                scrolled={scrolled}
                mobileMenuOpen={mobileMenuOpen}
              >
                Legal
              </NavLink>
              <DropdownContent isOpen={dropdownOpen || mobileMenuOpen}>
                <DropdownLink 
                  href="/privacy-policy.html"
                  onClick={() => {
                    setDropdownOpen(false);
                    setMobileMenuOpen(false);
                  }}
                >
                  Privacy Policy
                </DropdownLink>
                <DropdownLink 
                  href="/terms-and-conditions.html"
                  onClick={() => {
                    setDropdownOpen(false);
                    setMobileMenuOpen(false);
                  }}
                >
                  Terms & Conditions
                </DropdownLink>
              </DropdownContent>
            </DropdownContainer>
          </NavItem>
        </NavItems>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header; 